/* Copyright (c) 2020-present CloudIO Inc */

import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import zIndex from 'constants/ZIndex';
import useMove from 'hooks/useMove';
import React, { startTransition, useRef, useState } from 'react';
import { Theme } from '@mui/material';
import { flushSync } from 'react-dom';

type Side = 'l' | 'r' | 't' | 'b';

export default function ResizeHandler({
  side,
  size,
  min = 0,
  max = 10000,
  onMove,
  onResizeStart,
  onResizeStop,
  onMouseEnter,
  onMouseLeave,
}: {
  min?: number;
  max?: number;
  size: number;
  side: Side;
  onMove: (s: number) => void;
  onResizeStart?: () => void;
  onResizeStop?: () => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}): React.ReactElement {
  const originRef = useRef<number | undefined>(undefined);
  const classes = useStyles({ side });
  const [moving, setMoving] = useState(false);

  const moveProps = useMove('Resize', ({ status, offset }) => {
    if (status === 'start') {
      originRef.current = size;
      flushSync(() => {
        setMoving(true);
        if (onResizeStart) onResizeStart();
      });
    } else if (status === 'end') {
      originRef.current = undefined;
      flushSync(() => {
        setMoving(false);
        if (onResizeStop) onResizeStop();
      });
    } else if (originRef.current !== undefined) {
      startTransition(() => {
        if (originRef.current !== undefined) {
          // eslint-disable-next-line default-case
          switch (side) {
            case 'l':
              onMove(
                Math.min(Math.max(originRef.current - offset.x, min), max),
              );
              break;
            case 'r':
              onMove(
                Math.min(Math.max(originRef.current + offset.x, min), max),
              );
              break;
            case 't':
              onMove(
                Math.min(Math.max(originRef.current - offset.y, min), max),
              );
              break;
            case 'b':
              onMove(
                Math.min(Math.max(originRef.current + offset.y, min), max),
              );
              break;
          }
        }
      });
    }
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(
        'nodrag',
        classes.resizeHandler,
        moving ? classes.moving : null,
      )}
      {...moveProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDragStart={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  resizeHandler: ({ side }: { side: Side }) => ({
    position: 'absolute',
    zIndex: zIndex.ResizeHandler,
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
      transform: getScale(side),
    },
    ...getPosition(side),
  }),
  moving: ({ side }: { side: Side }) => ({
    backgroundColor: theme.palette.secondary.light,
    transform: getScale(side),
  }),
}));

function getScale(side: Side) {
  switch (side) {
    case 'l':
      return 'scale(3, 1) translateX(-1px)';
    case 'r':
      return 'scale(3, 1) translateX(1px)';
    case 't':
      return 'scale(1, 3)';
    case 'b':
      return 'scale(1, 3)';
    default:
      return 'unset';
  }
}

function getPosition(side: Side) {
  // eslint-disable-next-line default-case
  switch (side) {
    case 'l':
      return {
        cursor: 'col-resize',
        bottom: 0,
        left: 0,
        top: 0,
        width: 2,
      };
    case 'r':
      return {
        cursor: 'col-resize',
        bottom: 0,
        right: 0,
        top: 0,
        width: 2,
      };
    case 't':
      return {
        cursor: 'row-resize',
        height: 2,
        left: 0,
        top: 0,
        right: 0,
      };
    case 'b':
      return {
        cursor: 'row-resize',
        height: 2,
        left: 0,
        bottom: 0,
        right: 0,
      };
  }
}
